import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CrudSitesService } from '@features/admin/pages/crud-sites/crud-sites.service';
import {
  AddressViewModel,
  ContactViewModel,
  CreateFillSiteCommand,
  CreateSourceSiteCommand,
  EditFillSiteCommand,
  EditFillSiteSoilQualityTypeViewModel,
  EditSourceSiteCommand,
  SiteClassificationType,
  SiteStatus,
  SoilQualityType,
  VisibilityToClients
} from '@services/cleanfill-api/cleanfill-api.generated';
import { maxFieldLength } from '@validators/sync/max-length.validator';
import { IFillSiteFormGroupValue, ISourceSiteFormGroupValue } from '@view-models/site-management/site-create-form';
import { SiteCreateOption } from '@view-models/site-management/site-create-option';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { HttpClientsService } from '../http-clients/http-clients.service';
import { StoreClientsService } from '../store-clients/store-clients.service';

@Injectable({ providedIn: 'root' })
export class StoreAddSiteService {
  get DEFAULT_SOURCE_SITE_VALUE(): ISourceSiteFormGroupValue {
    return {
      status: SiteStatus.New,
      uniqueId: '',
      sourceSiteName: '',
      postalCode: '',
      address: '',
      lineOne: '',
      province: '',
      country: '',
      client: null,
      municipality: '',
      city: '',
      contactName: '',
      contactEmail: '',
      contactPhoneNumber: '',
      contactExtension: '',
      attachedDocuments: [],
      canSaveAsDraft: false,
      hasError: false
    };
  }
  get DEFAULT_FILL_SITE_VALUE(): IFillSiteFormGroupValue {
    return {
      status: SiteStatus.New,
      uniqueId: '',
      client: null,
      fillSiteName: '',
      postalCode: '',
      address: '',
      lineOne: '',
      province: '',
      country: '',
      municipality: '',
      city: '',
      // assignedQP: '',
      contactName: '',
      contactEmail: '',
      contactPhoneNumber: '',
      contactExtension: '',
      companyPhoneNumber: '',
      companyExtension: '',
      companyVisibility: VisibilityToClients.Public,
      tpqpApprovalRequired: true,
      // weightScaleAvailable: true,
      acceptedSoilTypes: [],
      siteClassificationTypes: undefined,
      attachedDocuments: [],
      canSaveAsDraft: false,
      hasError: false
    };
  }
  siteCreateOptionSubject$ = new BehaviorSubject<SiteCreateOption>(null);
  siteCreateOptionChanged$ = this.siteCreateOptionSubject$.asObservable().pipe(filter(t => t !== null));

  sourceSiteListChangedSubject$ = new BehaviorSubject<ISourceSiteFormGroupValue[]>([]);
  sourceSiteListChanged$ = this.sourceSiteListChangedSubject$.asObservable();

  fillSiteListChangedSubject$ = new BehaviorSubject<IFillSiteFormGroupValue[]>([]);
  fillSiteListChanged$ = this.fillSiteListChangedSubject$.asObservable();

  sourceSiteAddedSubject$ = new BehaviorSubject<ISourceSiteFormGroupValue>(null);
  sourceSiteAdded$ = this.sourceSiteAddedSubject$.asObservable().pipe(filter(t => t !== null));

  sourceSiteRemovedSubject$ = new BehaviorSubject<ISourceSiteFormGroupValue>(null);
  sourceSiteRemoved$ = this.sourceSiteRemovedSubject$.asObservable().pipe(filter(t => t !== null));

  fillSiteAddedSubject$ = new BehaviorSubject<IFillSiteFormGroupValue>(null);
  fillSiteAdded$ = this.fillSiteAddedSubject$.asObservable().pipe(filter(t => t !== null));

  fillSiteRemovedSubject$ = new BehaviorSubject<IFillSiteFormGroupValue>(null);
  fillSiteRemoved$ = this.fillSiteRemovedSubject$.asObservable().pipe(filter(t => t !== null));

  get siteCreateOption() {
    return this.siteCreateOptionSubject$.value;
  }

  get sourceSiteList() {
    return this.sourceSiteListChangedSubject$.value;
  }

  get fillSiteList() {
    return this.fillSiteListChangedSubject$.value;
  }

  get lastSourceSiteAdded() {
    return this.sourceSiteAddedSubject$.value;
  }

  get lastFillSiteAdded() {
    return this.fillSiteAddedSubject$.value;
  }
  complete = false;
  subscriptions = new Subscription();

  constructor(
    private crudSitesService: CrudSitesService,
    private clientStore: StoreClientsService,
    private fb: FormBuilder,
    private clientsHttp: HttpClientsService
  ) {}

  setOption(option: SiteCreateOption) {
    this.siteCreateOptionSubject$.next(option);
  }

  reset() {
    this.complete = false;
    this.siteCreateOptionSubject$.next(null);
    this.sourceSiteListChangedSubject$.next([]);
    this.sourceSiteAddedSubject$.next(null);
    this.sourceSiteRemovedSubject$.next(null);
    this.fillSiteListChangedSubject$.next([]);
    this.fillSiteAddedSubject$.next(null);
    this.fillSiteRemovedSubject$.next(null);
  }

  initSourceSiteFormGroup(value?: ISourceSiteFormGroupValue, isQPADM?: boolean) {
    value = value ?? this.DEFAULT_SOURCE_SITE_VALUE;

    const formGroup = this.fb.group({
      status: [value.status],
      uniqueId: [value.uniqueId],
      client: [value.client, isQPADM ? Validators.required : []],
      sourceSiteName: [value.sourceSiteName, [Validators.required, maxFieldLength(25)]],
      postalCode: [value.postalCode, Validators.required],
      address: [value.address, Validators.required],
      lineOne: [value.lineOne, Validators.required],
      province: [value.province, Validators.required],
      country: [value.country, Validators.required],
      municipality: [value.municipality],
      city: [value.city, Validators.required],
      contactName: [value.contactName, Validators.required],
      contactEmail: [value.contactEmail, [Validators.required, Validators.email]],
      contactPhoneNumber: [value.contactPhoneNumber, Validators.required],
      contactExtension: [value.contactExtension, [Validators.maxLength(5)]],
      attachedDocuments: this.fb.control(value.attachedDocuments),
      canSaveAsDraft: [false],
      hasError: [false]
    });

    return formGroup;
  }
  initSourceSiteFormGroupEvents(formGroup: FormGroup) {
    const subscriptions = [];
    /**
     * @desc create fill site if minimum requirements are valid
     */
    const createSourceSiteSub = formGroup.valueChanges.pipe(debounceTime(300)).subscribe(_ => {
      if (formGroup.invalid || !formGroup.get('uniqueId').value) {
        return;
      }

      this.editSourceSite(formGroup.value);
    });
    subscriptions.push(createSourceSiteSub);
    return subscriptions;
  }

  initFillSiteFormGroup(value?: IFillSiteFormGroupValue) {
    value = value ?? this.DEFAULT_FILL_SITE_VALUE;
    const formGroup = this.fb.group({
      status: [value.status],
      uniqueId: [value.uniqueId],
      fillSiteName: [value.fillSiteName, [Validators.required, maxFieldLength(25)]],
      postalCode: [value.postalCode, Validators.required],
      address: [value.address, Validators.required],
      lineOne: [value.lineOne, Validators.required],
      province: [value.province, Validators.required],
      country: [value.country, Validators.required],
      municipality: [value.municipality],
      city: [value.city, Validators.required],
      // assignedQP: [value.assignedQP, Validators.required],
      contactName: [value.contactName, Validators.required],
      contactEmail: [value.contactEmail, [Validators.required, Validators.email]],
      contactPhoneNumber: [value.contactPhoneNumber, Validators.required],
      contactExtension: [value.contactExtension, [Validators.maxLength(5)]],
      companyPhoneNumber: [value.companyPhoneNumber, Validators.required],
      companyExtension: [value.companyExtension],
      companyVisibility: [value.companyVisibility, Validators.required],
      tpqpApprovalRequired: [value.tpqpApprovalRequired, Validators.required],
      // weightScaleAvailable: [value.weightScaleAvailable, Validators.required],
      acceptedSoilTypes: this.fb.control(value.acceptedSoilTypes, [Validators.required, Validators.minLength(1)]),
      siteClassificationTypes: this.fb.control(value.siteClassificationTypes, [Validators.required, Validators.minLength(1)]),
      attachedDocuments: this.fb.control(value.attachedDocuments),
      canSaveAsDraft: [false],
      hasError: [false]
    });

    return formGroup;
  }
  initFillSiteFormGroupEvents(formGroup: FormGroup) {
    const subscriptions: Subscription[] = [];
    /**
     * @desc populate acceptedSoilTypes options based on selectedSoilClassification's field value
     */
    const selectedSoilClassification = formGroup.get('siteClassificationTypes').valueChanges.subscribe(value => {
      switch (value) {
        case SiteClassificationType.Class1SoilManagementSite:
        case SiteClassificationType.LandFillingSiteOrDump:
          formGroup.patchValue({
            // acceptedSoilTypes: [new EditFillSiteSoilQualityTypeViewModel({ soilQualityType: SoilQualityType.AllSoilTypes })]
            acceptedSoilTypes: Object.keys(SoilQualityType).map(
              key => new EditFillSiteSoilQualityTypeViewModel({ soilQualityType: SoilQualityType[key] })
            )
          });
          break;
        case SiteClassificationType.ReUseSite:
        case SiteClassificationType.Class2SoilManagementSite:
          formGroup.patchValue({
            acceptedSoilTypes: [
              new EditFillSiteSoilQualityTypeViewModel({ soilQualityType: SoilQualityType.OReg153Table1 }),
              new EditFillSiteSoilQualityTypeViewModel({ soilQualityType: SoilQualityType.OReg153Table2 }),
              new EditFillSiteSoilQualityTypeViewModel({ soilQualityType: SoilQualityType.OReg153Table3 })
            ]
          });
          break;
        default:
          break;
      }
    });

    subscriptions.push(selectedSoilClassification);

    /**
     * @desc create fill site if minimum requirements are valid
     */
    const createFillSiteSub = formGroup.valueChanges.pipe(debounceTime(300)).subscribe(_ => {
      if (formGroup.invalid || !formGroup.get('uniqueId').value) {
        return;
      }

      this.editFillSite(formGroup.value);
    });
    subscriptions.push(createFillSiteSub);

    return subscriptions;
  }

  addSourceSite(sourceSite?: ISourceSiteFormGroupValue): Promise<ISourceSiteFormGroupValue> {
    sourceSite = sourceSite || this.DEFAULT_SOURCE_SITE_VALUE;

    this.sourceSiteListChangedSubject$.next([...this.sourceSiteList, sourceSite]);
    this.sourceSiteAddedSubject$.next(sourceSite);

    return Promise.resolve(sourceSite);
  }
  createSourceSite(index: number) {
    const sourceSite = this.sourceSiteList[index];

    const payload = new CreateSourceSiteCommand({
      companyUniqueId: this.clientStore.companySelected?.companyUniqueId,
      address: new AddressViewModel({
        city: sourceSite.city,
        postalCode: sourceSite.postalCode,
        provinceUniqueId: sourceSite.province || '00000000-0000-0000-0000-000000000000',
        line1: sourceSite.lineOne
      }),
      contact: new ContactViewModel({
        name: sourceSite.contactName,
        email: sourceSite.contactEmail,
        phoneNumber: sourceSite.contactPhoneNumber,
        extension: sourceSite.contactExtension
      }),
      name: sourceSite.sourceSiteName,
      municipalityName: sourceSite.municipality,
      status: SiteStatus.New
    });
    return this.crudSitesService.createSourceSite(payload).then(t => {
      sourceSite.uniqueId = t;
      return t;
    });
  }

  addFillSite(fillSite?: IFillSiteFormGroupValue) {
    fillSite = fillSite || this.DEFAULT_FILL_SITE_VALUE;
    this.fillSiteListChangedSubject$.next([...this.fillSiteList, fillSite]);
    this.fillSiteAddedSubject$.next(fillSite);
    return Promise.resolve(fillSite);
  }

  createFillSite(index: number) {
    const fillSite = this.fillSiteList[index];
    const payload = new CreateFillSiteCommand({
      companyUniqueId: this.clientStore.companySelected?.companyUniqueId,
      address: new AddressViewModel({
        city: fillSite.city,
        postalCode: fillSite.postalCode,
        provinceUniqueId: fillSite.province || '00000000-0000-0000-0000-000000000000',
        line1: fillSite.lineOne
      }),
      contact: new ContactViewModel({
        name: fillSite.contactName,
        email: fillSite.contactEmail,
        phoneNumber: fillSite.contactPhoneNumber,
        extension: fillSite.contactExtension
      }),
      name: fillSite.fillSiteName,
      siteClassificationType: SiteClassificationType.Class1SoilManagementSite,
      soilQualityTypes: fillSite.acceptedSoilTypes.map(type => type.soilQualityType),
      isTPQPApprovalRequired: false,
      // isWeightScaleAvailable: false,
      visibilityToClients: fillSite.companyVisibility,
      municipalityName: fillSite.municipality,
      status: SiteStatus.New
    });
    return this.crudSitesService.createFillSite(payload).then(t => {
      fillSite.uniqueId = t;
      return t;
    });
  }
  editSourceSite(sourceSite: ISourceSiteFormGroupValue, status?: SiteStatus) {
    if (!sourceSite.uniqueId) {
      return;
    }

    const payload = new EditSourceSiteCommand({
      status: status || sourceSite.status,
      uniqueId: sourceSite.uniqueId,
      address: new AddressViewModel({
        city: sourceSite.city,
        postalCode: sourceSite.postalCode,
        provinceUniqueId: sourceSite.province,
        line1: sourceSite.lineOne
      }),
      contact: new ContactViewModel({
        name: sourceSite.contactName,
        email: sourceSite.contactEmail,
        phoneNumber: sourceSite.contactPhoneNumber,
        extension: sourceSite.contactExtension
      }),
      name: sourceSite.sourceSiteName,
      municipalityName: sourceSite.municipality
    });

    return this.crudSitesService.editSourceSite(payload);
  }
  editFillSite(fillSite: IFillSiteFormGroupValue, status?: SiteStatus) {
    if (!fillSite.uniqueId) {
      return;
    }

    const payload = new EditFillSiteCommand({
      status: status || fillSite.status,
      uniqueId: fillSite.uniqueId,
      address: new AddressViewModel({
        city: fillSite.city,
        postalCode: fillSite.postalCode,
        provinceUniqueId: fillSite.province || '00000000-0000-0000-0000-000000000000',
        line1: fillSite.lineOne
      }),
      contact: new ContactViewModel({
        name: fillSite.contactName,
        email: fillSite.contactEmail,
        phoneNumber: fillSite.contactPhoneNumber,
        extension: fillSite.contactExtension
      }),
      name: fillSite.fillSiteName,
      siteClassificationType: fillSite.siteClassificationTypes || ('0' as SiteClassificationType),
      soilQualityTypes: fillSite.acceptedSoilTypes,
      isTPQPApprovalRequired: false,
      // isWeightScaleAvailable: false,
      visibilityToClients: fillSite.companyVisibility,
      municipalityName: fillSite.municipality
    });

    return this.crudSitesService.editFillSite(payload);
  }

  deleteSourceSite(uniqueId) {
    let sourceSite = null;
    const sourceSiteList = this.sourceSiteList;
    console.log(sourceSiteList);
    const index = sourceSiteList.findIndex(x => x.uniqueId === uniqueId);
    if (index >= 0) {
      sourceSite = sourceSiteList.splice(index, 1)[0];
      this.sourceSiteListChangedSubject$.next([...sourceSiteList]);
      this.sourceSiteRemovedSubject$.next(sourceSite);
    }

    return sourceSite;
  }
  deleteAllSourceSites() {
    const sourceSiteList = this.sourceSiteList;
    this.sourceSiteListChangedSubject$.next([]);
    sourceSiteList.forEach(t => this.sourceSiteRemovedSubject$.next(t));
  }

  deleteAllFillSites() {
    const fillSiteList = this.fillSiteList;
    this.fillSiteListChangedSubject$.next([]);
    fillSiteList.forEach(t => this.fillSiteRemovedSubject$.next(t));
  }

  deleteFillSite(uniqueId) {
    let fillSite = null;
    const fillSiteList = this.fillSiteList;
    const index = fillSiteList.findIndex(x => x.uniqueId === uniqueId);
    if (index >= 0) {
      fillSite = fillSiteList.splice(index, 1)[0];
      this.fillSiteListChangedSubject$.next([...fillSiteList]);
      this.fillSiteRemovedSubject$.next(fillSite);
    }

    return fillSite;
  }
}
